import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "@/config/config";
import {
  divideHealthItems,
  getStockCounts,
  transferItem,
  transferItems,
  unwrapBulletItems,
  unwrapCreditsItem,
  unwrapHealthItems,
  unwrapKeyItem,
  unwrapMafiaItem,
  unwrapOGCrate,
  unwrapOGNFTItem,
  unwrapOGNFT,
  wrapOGCrate,
  wrapOGNFT,
} from "@/helper/contractFunctions/MafiaInventory";
import { transferErc1155 } from "@/helper/contractFunctions";
import { unwrapFounderTableShareItems } from "@/helper/contractFunctions/FounderTableShare";
import { unwrapGICreditsItems } from "@/helper/contractFunctions/MafiaGICredit";
import { crateBalanceOf, setCrateApprovalForAll } from "@/helper/contractFunctions/OgCrate";
import { ogNFTBalance, setOGNFTApprovalForAll } from "@/helper/contractFunctions/OGNFT";
import { parseInventoryItemInfos, parseInventoryItems } from "@/helper/profile";
import { InventoryItemInfo } from "@/types/InventoryItemInfo";
import InventorySlotInfo from "@/types/InventorySlotInfo";
import { ItemInfo } from "@/types/ItemInfo";

export interface InventoryState {
  crateBalance: number;
  ogNFTBalance: number;
  isApprovingCrate: boolean;
  isApprovingOGNFT: boolean;
  myInventoryItems: ItemInfo[];
  mySlotItems: InventorySlotInfo[];
  isLoadingMyInventoryItems: boolean;
  myInventoryOwnerUserId: number | null;
  myInventoryRequestId: string | null;
  inventoryItems: ItemInfo[];
  slotItems: InventorySlotInfo[];
  isLoadingInventoryItems: boolean;
  isTransferringItem: boolean;
  isTransferringCrate: boolean;
  isWrappingOgCrate: boolean;
  isUnwrappingOgCrate: boolean;
  isWrappingOGNFTAction: boolean;
  isUnwrappingOGNFTAction: boolean;
  isUnwrappingKeyItem: boolean;
  isUnwrappingMafiaItem: boolean;
  isUnwrappingOGNFTItem: boolean;
  isUnwrappingBulletItem: boolean;
  isUnwrappingHealth: boolean;
  isUnwrappingHelperCreditsItem: boolean;
  isUnwrappingGICreditsItems: boolean;
  isUnwrappingFounderTableShareItems: boolean;
  inventoryCategoryStocks: number[];
  isLoadingInventoryCategoryStocks: boolean;
  inventoryItemStocks: number[][];
  isLoadingInventoryItemStocks: boolean;
  inventoryItemInfos: InventoryItemInfo[];
  isLoadingInventoryItemInfos: boolean;
  isDividingHealthItems: boolean;
}

const initialState: InventoryState = {
  crateBalance: 0,
  ogNFTBalance: 0,
  isApprovingCrate: false,
  isApprovingOGNFT: false,
  myInventoryItems: [],
  mySlotItems: [],
  isLoadingMyInventoryItems: false,
  myInventoryOwnerUserId: null,
  myInventoryRequestId: null,
  inventoryItems: [],
  slotItems: [],
  isLoadingInventoryItems: false,
  isTransferringItem: false,
  isTransferringCrate: false,
  isWrappingOgCrate: false,
  isUnwrappingOgCrate: false,
  isWrappingOGNFTAction: false,
  isUnwrappingOGNFTAction: false,
  isUnwrappingKeyItem: false,
  isUnwrappingMafiaItem: false,
  isUnwrappingOGNFTItem: false,
  isUnwrappingBulletItem: false,
  isUnwrappingHealth: false,
  isUnwrappingHelperCreditsItem: false,
  isUnwrappingGICreditsItems: false,
  isUnwrappingFounderTableShareItems: false,
  inventoryCategoryStocks: [],
  isLoadingInventoryCategoryStocks: false,
  inventoryItemStocks: [],
  isLoadingInventoryItemStocks: false,
  inventoryItemInfos: [],
  isLoadingInventoryItemInfos: false,
  isDividingHealthItems: false,
};

export const getCrateBalance = createAsyncThunk("inventory/getCrateBalance", async ({ account }: { account: string }) => Number(await crateBalanceOf(account)));
export const getOgNFTBalance = createAsyncThunk("inventory/getOgNFTBalance", async ({ account }: { account: string }) => Number(await ogNFTBalance(account)));
export const approveAllCrate = createAsyncThunk("inventory/approveAllCrate", async ({ spender, account }: { spender: string; account: string }) => { await setCrateApprovalForAll(spender, true, account); });
export const approveAllOGNFT = createAsyncThunk("inventory/approveAllOGNFT", async ({ address, account }: { address: string; account: string }) => { await setOGNFTApprovalForAll(address, true, account); });
export const getMyInventoryItems = createAsyncThunk(
  "inventory/getMyInventoryItems",
  async ({ userId }: { userId: number }) => {
    const response = await axios.get(
      SERVER_URL + "/profile/inventory/" + userId
    );
    return response.data;
  }
);
export const getInventoryItem = createAsyncThunk(
  "inventory/getInventoryItem",
  async ({ userId }: { userId: number }) => {
    const response = await axios.get(SERVER_URL + "/profile/inventory/" + userId);
    return response.data;
  }
);

export const transferInventoryItem = createAsyncThunk(
  "inventory/transferInventoryItem",
  async ({ to, account, itemId }: { to: string; account: string; itemId: number }) => {
    await transferItem(account, to, itemId);
  }
);

export const bulkTransferInventoryItems = createAsyncThunk(
  "inventory/bulkTransferInventoryItems",
  async ({ to, account, itemIds }: { to: string; account: string; itemIds: number[] }) => {
    await transferItems(account, to, itemIds);
  }
);

export const transferNFT = createAsyncThunk(
  "inventory/transferNFT",
  async ({ to, account, amount, contractAddress }: { to: string; account: string; amount: number; contractAddress: string }) => {
    await transferErc1155(account, to, amount, contractAddress);
  }
);
export const wrapOGCrateAction = createAsyncThunk(
  "inventory/wrapOGCrateAction",
  async ({ account }: { account: string }) => {
    await wrapOGCrate(account);
  }
);

export const unwrapOGCrateAction = createAsyncThunk(
  "inventory/unwrapOGCrateAction",
  async ({ account, itemId }: { account: string; itemId: number }) => {
    await unwrapOGCrate(account, itemId);
  }
);

export const wrapOGNFTAction = createAsyncThunk(
  "inventory/wrapOGNFTAction",
  async ({ account }: { account: string }) => {
    await wrapOGNFT(account);
  }
);

export const unwrapOGNFTAction = createAsyncThunk(
  "inventory/unwrapOGNFTAction",
  async ({ account, itemId }: { account: string; itemId: number }) => {
    await unwrapOGNFT(account, itemId);
  }
);
export const unwrapKeyItemAction = createAsyncThunk(
  "inventory/unwrapKeyItemAction",
  async ({ account, itemId }: { account: string; itemId: number }) => {
    await unwrapKeyItem(account, itemId);
  }
);

export const unwrapMafiaItemAction = createAsyncThunk(
  "inventory/unwrapMafiaItemAction",
  async ({ account, itemId }: { account: string; itemId: number }) => {
    await unwrapMafiaItem(account, itemId);
  }
);

export const unwrapOGNFTItemAction = createAsyncThunk(
  "inventory/unwrapOGNFTItemAction",
  async ({ account, itemId }: { account: string; itemId: number }) => {
    await unwrapOGNFTItem(account, itemId);
  }
);
export const unwrapBulletAction = createAsyncThunk(
  "inventory/unwrapBulletAction",
  async ({ account, itemIds }: { account: string; itemIds: number[] }) => {
    await unwrapBulletItems(account, itemIds);
  }
);

export const unwrapHealthAction = createAsyncThunk(
  "inventory/unwrapHealthAction",
  async ({ account, itemIds }: { account: string; itemIds: number[] }) => {
    await unwrapHealthItems(account, itemIds);
  }
);
export const unwrapCreditsItemAction = createAsyncThunk(
  "inventory/unwrapCreditsItemAction",
  async ({ account, itemIds }: { account: string; itemIds: number[] }) => {
    await unwrapCreditsItem(account, itemIds);
  }
);

export const unwrapGICreditsItemsAction = createAsyncThunk(
  "inventory/unwrapGICreditsItemsAction",
  async ({ account, items }: { account: string; items: number[] }) => {
    const data = await unwrapGICreditsItems(items, account);
    return data.transactionHash;
  }
);

export const unwrapFounderTableShareItemsAction = createAsyncThunk(
  "inventory/unwrapFounderTableShareItemsAction",
  async ({ account, itemIds }: { account: string; itemIds: number[] }) => {
    await unwrapFounderTableShareItems(account, itemIds);
  }
);
export const getItemInfosAction = createAsyncThunk(
  "inventory/getItemInfosAction",
  async () => {
    const response = await axios.get(SERVER_URL + "/profile/inventoryInfo");
    return response.data;
  }
);

export const getInventoryStocks = createAsyncThunk(
  "inventory/getInventoryStocks",
  async () => {
    const { categoryStocks, itemStocks } = await getStockCounts();
    return { categoryStocks, itemStocks };
  }
);

export const divideHealthItemsAction = createAsyncThunk(
  "inventory/divideHealthItemsAction",
  async ({ account, itemIds }: { account: string; itemIds: number[] }) => {
    const data = await divideHealthItems(account, itemIds);
    return data.transactionHash;
  }
);

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    clearMyInventory: (state) => {
      state.myInventoryItems = [];
      state.mySlotItems = [];
      state.isLoadingMyInventoryItems = false;
      state.myInventoryOwnerUserId = null;
      state.myInventoryRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCrateBalance.fulfilled, (state, { payload }) => { state.crateBalance = payload; })
      .addCase(getOgNFTBalance.fulfilled, (state, { payload }) => { state.ogNFTBalance = payload; })
      .addCase(approveAllCrate.pending, (state) => { state.isApprovingCrate = true; })
      .addCase(approveAllCrate.fulfilled, (state) => { state.isApprovingCrate = false; })
      .addCase(approveAllCrate.rejected, (state) => { state.isApprovingCrate = false; })
      .addCase(approveAllOGNFT.pending, (state) => { state.isApprovingOGNFT = true; })
      .addCase(approveAllOGNFT.fulfilled, (state) => { state.isApprovingOGNFT = false; })
      .addCase(approveAllOGNFT.rejected, (state) => { state.isApprovingOGNFT = false; })      .addCase(getMyInventoryItems.pending, (state, { meta }) => {
        state.myInventoryItems = [];
        state.mySlotItems = [];
        state.isLoadingMyInventoryItems = true;
        state.myInventoryOwnerUserId = meta.arg.userId;
        state.myInventoryRequestId = meta.requestId;
      })
      .addCase(getMyInventoryItems.fulfilled, (state, { payload, meta }) => {
        if (
          state.myInventoryOwnerUserId !== meta.arg.userId ||
          state.myInventoryRequestId !== meta.requestId
        ) {
          return;
        }

        const parsedItems = parseInventoryItems(payload);
        state.myInventoryItems = parsedItems.inventoryItems;
        state.mySlotItems = parsedItems.slotItems;
        state.isLoadingMyInventoryItems = false;
        state.myInventoryRequestId = null;
      })
      .addCase(getMyInventoryItems.rejected, (state, { meta }) => {
        if (state.myInventoryRequestId !== meta.requestId) return;
        state.isLoadingMyInventoryItems = false;
        state.myInventoryRequestId = null;
      })
      .addCase(getInventoryItem.pending, (state) => {
        state.isLoadingInventoryItems = true;
      })
      .addCase(getInventoryItem.fulfilled, (state, { payload }) => {
        const parsedItems = parseInventoryItems(payload);
        state.inventoryItems = parsedItems.inventoryItems;
        state.slotItems = parsedItems.slotItems;
        state.isLoadingInventoryItems = false;

        console.log(parsedItems);
      })
      .addCase(getInventoryItem.rejected, (state) => {
        state.isLoadingInventoryItems = false;
      })
      .addCase(transferInventoryItem.pending, (state) => {
        state.isTransferringItem = true;
      })
      .addCase(transferInventoryItem.fulfilled, (state) => {
        state.isTransferringItem = false;
      })
      .addCase(transferInventoryItem.rejected, (state) => {
        state.isTransferringItem = false;
      })
      .addCase(bulkTransferInventoryItems.pending, (state) => {
        state.isTransferringItem = true;
      })
      .addCase(bulkTransferInventoryItems.fulfilled, (state) => {
        state.isTransferringItem = false;
      })
      .addCase(bulkTransferInventoryItems.rejected, (state) => {
        state.isTransferringItem = false;
      })
      .addCase(transferNFT.pending, (state) => {
        state.isTransferringCrate = true;
      })
      .addCase(transferNFT.fulfilled, (state) => {
        state.isTransferringCrate = false;
      })
      .addCase(transferNFT.rejected, (state) => {
        state.isTransferringCrate = false;
      })      .addCase(wrapOGCrateAction.pending, (state) => {
        state.isWrappingOgCrate = true;
      })
      .addCase(wrapOGCrateAction.fulfilled, (state) => {
        state.isWrappingOgCrate = false;
      })
      .addCase(wrapOGCrateAction.rejected, (state) => {
        state.isWrappingOgCrate = false;
      })
      .addCase(unwrapOGCrateAction.pending, (state) => {
        state.isUnwrappingOgCrate = true;
      })
      .addCase(unwrapOGCrateAction.fulfilled, (state) => {
        state.isUnwrappingOgCrate = false;
      })
      .addCase(unwrapOGCrateAction.rejected, (state) => {
        state.isUnwrappingOgCrate = false;
      })
      .addCase(wrapOGNFTAction.pending, (state) => {
        state.isWrappingOGNFTAction = true;
      })
      .addCase(wrapOGNFTAction.fulfilled, (state) => {
        state.isWrappingOGNFTAction = false;
      })
      .addCase(wrapOGNFTAction.rejected, (state) => {
        state.isWrappingOGNFTAction = false;
      })
      .addCase(unwrapOGNFTAction.pending, (state) => {
        state.isUnwrappingOGNFTAction = true;
      })
      .addCase(unwrapOGNFTAction.fulfilled, (state) => {
        state.isUnwrappingOGNFTAction = false;
      })
      .addCase(unwrapOGNFTAction.rejected, (state) => {
        state.isUnwrappingOGNFTAction = false;
      })      .addCase(unwrapKeyItemAction.pending, (state) => {
        state.isUnwrappingKeyItem = true;
      })
      .addCase(unwrapKeyItemAction.fulfilled, (state) => {
        state.isUnwrappingKeyItem = false;
      })
      .addCase(unwrapKeyItemAction.rejected, (state) => {
        state.isUnwrappingKeyItem = false;
      })
      .addCase(unwrapMafiaItemAction.pending, (state) => {
        state.isUnwrappingMafiaItem = true;
      })
      .addCase(unwrapMafiaItemAction.fulfilled, (state) => {
        state.isUnwrappingMafiaItem = false;
      })
      .addCase(unwrapMafiaItemAction.rejected, (state) => {
        state.isUnwrappingMafiaItem = false;
      })
      .addCase(unwrapOGNFTItemAction.pending, (state) => {
        state.isUnwrappingOGNFTItem = true;
      })
      .addCase(unwrapOGNFTItemAction.fulfilled, (state) => {
        state.isUnwrappingOGNFTItem = false;
      })
      .addCase(unwrapOGNFTItemAction.rejected, (state) => {
        state.isUnwrappingOGNFTItem = false;
      })      .addCase(unwrapBulletAction.pending, (state) => {
        state.isUnwrappingBulletItem = true;
      })
      .addCase(unwrapBulletAction.fulfilled, (state) => {
        state.isUnwrappingBulletItem = false;
      })
      .addCase(unwrapBulletAction.rejected, (state) => {
        state.isUnwrappingBulletItem = false;
      })
      .addCase(unwrapHealthAction.pending, (state) => {
        state.isUnwrappingHealth = true;
      })
      .addCase(unwrapHealthAction.fulfilled, (state) => {
        state.isUnwrappingHealth = false;
      })
      .addCase(unwrapHealthAction.rejected, (state) => {
        state.isUnwrappingHealth = false;
      })      .addCase(unwrapCreditsItemAction.pending, (state) => {
        state.isUnwrappingHelperCreditsItem = true;
      })
      .addCase(unwrapCreditsItemAction.fulfilled, (state) => {
        state.isUnwrappingHelperCreditsItem = false;
      })
      .addCase(unwrapCreditsItemAction.rejected, (state) => {
        state.isUnwrappingHelperCreditsItem = false;
      })
      .addCase(unwrapGICreditsItemsAction.pending, (state) => {
        state.isUnwrappingGICreditsItems = true;
      })
      .addCase(unwrapGICreditsItemsAction.fulfilled, (state) => {
        state.isUnwrappingGICreditsItems = false;
      })
      .addCase(unwrapGICreditsItemsAction.rejected, (state) => {
        state.isUnwrappingGICreditsItems = false;
      })
      .addCase(unwrapFounderTableShareItemsAction.pending, (state) => {
        state.isUnwrappingFounderTableShareItems = true;
      })
      .addCase(unwrapFounderTableShareItemsAction.fulfilled, (state) => {
        state.isUnwrappingFounderTableShareItems = false;
      })
      .addCase(unwrapFounderTableShareItemsAction.rejected, (state) => {
        state.isUnwrappingFounderTableShareItems = false;
      })      .addCase(getItemInfosAction.pending, (state) => {
        state.isLoadingInventoryItemInfos = true;
      })
      .addCase(getItemInfosAction.fulfilled, (state, { payload }) => {
        state.inventoryItemInfos = parseInventoryItemInfos(payload);
        state.isLoadingInventoryItemInfos = false;
      })
      .addCase(getItemInfosAction.rejected, (state) => {
        state.isLoadingInventoryItemInfos = false;
      })
      .addCase(getInventoryStocks.pending, (state) => {
        state.isLoadingInventoryCategoryStocks = true;
        state.isLoadingInventoryItemStocks = true;
      })
      .addCase(getInventoryStocks.fulfilled, (state, { payload }) => {
        state.inventoryCategoryStocks = payload.categoryStocks;
        state.inventoryItemStocks = payload.itemStocks;
        state.isLoadingInventoryItemStocks = false;
        state.isLoadingInventoryCategoryStocks = false;
      })
      .addCase(getInventoryStocks.rejected, (state) => {
        state.isLoadingInventoryItemStocks = false;
        state.isLoadingInventoryCategoryStocks = false;
      })
      .addCase(divideHealthItemsAction.pending, (state) => {
        state.isDividingHealthItems = true;
      })
      .addCase(divideHealthItemsAction.fulfilled, (state) => {
        state.isDividingHealthItems = false;
      })
      .addCase(divideHealthItemsAction.rejected, (state) => {
        state.isDividingHealthItems = false;
      });
  },
});

export const { clearMyInventory } = inventorySlice.actions;
export default inventorySlice.reducer;
